import React from 'react';
import { Download, Chrome, Globe, CheckCircle, AlertTriangle, Crown, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GmailPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
      <SEOHead 
        title="Extension Gmail" 
        description="Installez l'extension SENDBOX pour Google Chrome et Gmail. Partagez des fichiers volumineux directement depuis votre boîte Gmail." 
      />
      <NavigationMenu />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">

        <div className="flex items-center gap-4 mb-8">
          <img src={`${BACKEND_URL}/gmail/icon-64.png`} alt="SENDBOX" className="w-14 h-14 rounded-xl" />
          <div>
            <h1 className="text-2xl font-bold">Extension Gmail</h1>
            <p className="text-blue-300/60 text-sm">Envoyez des fichiers volumineux directement depuis Gmail</p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            <Crown className="w-3 h-3" /> PRO
          </span>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { icon: <Chrome className="w-5 h-5" />, title: 'Google Chrome', desc: 'Extension Chrome officielle' },
            { icon: <Globe className="w-5 h-5" />, title: 'Gmail Web', desc: 'mail.google.com' },
            { icon: <CheckCircle className="w-5 h-5" />, title: 'Intégration directe', desc: 'Bouton dans la barre de composition' },
          ].map((f, i) => (
            <div key={i} className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 text-center">
              <div className="text-blue-400 mb-2 flex justify-center">{f.icon}</div>
              <h3 className="text-white font-medium text-sm">{f.title}</h3>
              <p className="text-blue-300/50 text-xs">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Download */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 mb-8" data-testid="gmail-download-section">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-400" /> Téléchargement
          </h2>
          <p className="text-blue-300/60 text-sm mb-4">
            Téléchargez l'extension Gmail au format ZIP. L'extension est pré-configurée pour votre domaine SENDBOX.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`${BACKEND_URL}/api/extensions/gmail-zip`}
              download="SENDBOX_Gmail_Extension.zip"
              className="flex-1"
              data-testid="gmail-download-zip-btn"
            >
              <Button className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white">
                <Download className="w-4 h-4 mr-2" /> Télécharger l'extension (.zip)
              </Button>
            </a>
            <a
              href={`${BACKEND_URL}/gmail/popup.html`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              data-testid="gmail-test-popup-btn"
            >
              <Button variant="outline" className="w-full border-blue-500/30 text-blue-300">
                <ExternalLink className="w-4 h-4 mr-2" /> Tester dans le navigateur
              </Button>
            </a>
          </div>
        </div>

        {/* Installation Guide */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 mb-8" data-testid="gmail-install-guide">
          <h2 className="text-lg font-semibold text-white mb-6">Guide d'installation</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Installation dans Chrome</h3>
              <ol className="space-y-2">
                {[
                  'Téléchargez l\'extension en cliquant sur le bouton ci-dessus',
                  'Décompressez le fichier ZIP téléchargé',
                  'Ouvrez Chrome et accédez à chrome://extensions',
                  'Activez le "Mode développeur" en haut à droite',
                  'Cliquez sur "Charger l\'extension non empaquetée"',
                  'Sélectionnez le dossier "SENDBOX_Gmail" décompressé',
                  'L\'extension SENDBOX apparaît dans la barre d\'extensions Chrome',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-blue-300/70 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="border-t border-blue-500/10 pt-6">
              <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Utilisation dans Gmail</h3>
              <ol className="space-y-2">
                {[
                  'Ouvrez Gmail et composez un nouveau message',
                  'Cliquez sur l\'icône SENDBOX dans la barre d\'extensions Chrome',
                  'Connectez-vous avec votre compte Pro SENDBOX',
                  'Glissez-déposez ou sélectionnez vos fichiers',
                  'Cliquez sur "Envoyer via SENDBOX"',
                  'Le lien de téléchargement est automatiquement copié pour votre email',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-blue-300/70 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 flex gap-3" data-testid="gmail-pro-note">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-300 text-sm font-medium">Abonnement Pro requis</p>
            <p className="text-blue-300/60 text-xs mt-1">Cette extension nécessite un compte SENDBOX Pro actif. Les mêmes limites que votre plan Pro s'appliquent (expiration, téléchargements max, etc.).</p>
          </div>
        </div>
      </div>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default GmailPage;
